import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { AccountDetailsLayout } from "@common/Layout/AccountDetailsLayout";
import { PaymentModal } from "@common/Modal/PaymentModal";
import { EPAYMENT_INITIALISE_TYPES, EPAYMENT_TYPES_NAME, TITLE_INHERIT_STYLE, WORDPRESS_URL } from "@constants";
import { SelectedServiceContext } from "@context/ISelectedContext";
import { IFinalizeOrderResponse } from "@features/Payment/CurrentBalance/hook/usePaymentBalance";
import { useResetCreditLimit } from "@features/ResetCreditLimit/hook/useResetCreditLimit";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Checkbox,
    FormControlLabel,
    Grid,
    Link,
    Typography,
} from "@material-ui/core";
import { getLanguage } from "@utils/changeLanguage";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CardPayment from "../../features/Payment/ManageCards/CardPayment";

const ResetCreditLimitPage = () => {
    const { t } = useTranslation(["accountDetails", "payment"]);
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const [isPaymentOpen, setIsPaymentOpen] = useState<boolean>(false);
    const { selectedService } = useContext(SelectedServiceContext);
    const { resetCreditLimitState, finaliseResetCreditLimitPayment } = useResetCreditLimit();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<IFinalizeOrderResponse>({
        success: false,
        orderNumber: "",
        paymentRef: "",
        file: {
            name: "",
            base64File: "",
        },
        prospectId: "",
    });

    const finalisePaymentCallback = (modalData: IFinalizeOrderResponse) => {
        setModalData(modalData);
        setIsModalOpen(true);
    };

    const termsAndConditionsSentence = (
        <Link
            color="secondary"
            href={`${WORDPRESS_URL}/${getLanguage()}/legal`}
            rel="noopener"
            target="_blank"
            underline="always"
        >
            <b> {t("accountDetails:termsAndConditions")}</b>
        </Link>
    );

    const handlePayNowClick = () => {
        setIsPaymentOpen(true);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        window.history.replaceState({}, document.title, window.location.href.split("?")[0]);

        const finalisePayment = async () => {
            const orderId = searchParams.get("orderId");
            if (orderId && selectedService)
                finaliseResetCreditLimitPayment(orderId, selectedService.id, finalisePaymentCallback);
        };

        finalisePayment();
    }, [selectedService]);

    return (
        <AccountDetailsLayout requireNumberSelection title={t("accountDetails:resetCreditCardLimit")}>
            {selectedService && (
                <>
                    <Card elevation={2}>
                        <CardHeader
                            className="card-header card-header__right"
                            title={t("accountDetails:resetCreditCardLimit")}
                            titleTypographyProps={TITLE_INHERIT_STYLE}
                            style={{ backgroundColor: "#4a148c" }}
                        />
                        <CardContent>
                            <div className="card-header__circle" style={{ backgroundColor: "#4a148c" }} />
                            <HandleErrorAndLoading
                                error={resetCreditLimitState.error}
                                isLoading={resetCreditLimitState.isLoading}
                            >
                                <Grid container justify="center" alignItems="center" spacing={1}>
                                    <Grid item xs={12} md={9} style={{ textAlign: "center" }}>
                                        <Typography color="primary" variant="h2">
                                            {"€10"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={9}>
                                        <Typography color="secondary" variant="body2">
                                            {t("accountDetails:resetLimitInfo")}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={termsChecked}
                                                    name={"termsAndConditions"}
                                                    onChange={(event) => setTermsChecked(event.target.checked)}
                                                />
                                            }
                                            disabled={isPaymentOpen}
                                            label={termsAndConditionsSentence}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container justify="center" style={{ marginTop: "15px" }}>
                                    <Grid item>
                                        <Button
                                            disabled={!termsChecked || isPaymentOpen}
                                            color="secondary"
                                            type="button"
                                            variant="outlined"
                                            onClick={handlePayNowClick}
                                        >
                                            {t("payment:payNow")}
                                        </Button>
                                    </Grid>
                                </Grid>
                                {isPaymentOpen && (
                                    <CardPayment
                                        serviceId={selectedService.id}
                                        isOnceOffPayment={true}
                                        loadDefaultCard={true}
                                        paymentType={EPAYMENT_INITIALISE_TYPES.RESET_CREDIT_LIMIT}
                                    />
                                )}
                            </HandleErrorAndLoading>
                        </CardContent>
                        <CardActions className="card-footer" style={{ backgroundColor: "#4a148c" }}>
                            <Typography align="center" color="inherit" variant="body1">
                                {t("payment:paymentMethod")}: {t(`payment:${EPAYMENT_TYPES_NAME.CREDIT_CARD}` as any)}
                            </Typography>
                        </CardActions>
                    </Card>
                    <PaymentModal
                        success={modalData.success}
                        paymentRef={modalData.paymentRef}
                        prospectId={modalData.prospectId}
                        file={modalData.file}
                        isModalOpen={isModalOpen}
                        onConfirm={() => {
                            setIsModalOpen(false);
                        }}
                    />
                </>
            )}
        </AccountDetailsLayout>
    );
};

export default ResetCreditLimitPage;
